import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Check, AlertCircle, Calculator, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PricingCalculator from '../components/PricingCalculator';
import MembershipPerks from '../components/MembershipPerks';

const AnimatedSection = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const Membership = () => {
  const navigate = useNavigate();
  const [showCalculator, setShowCalculator] = useState(false);

  const membershipPlans = [
    {
      name: 'Fitness Only',
      description: 'Unlimited fitness classes (HIIT, strength, etc.) + gym access, 7 days a week + unlimited self-help ice baths',
      prices: {
        '1 Month': 2950,
        '3 Month': 7965,
        '6 Month': 15045,
        '12 Month': 26550,
      },
    },
    {
      name: 'Cobra Thai Til I Die',
      description: 'Unlimited Martial Arts & Fitness classes + gym access, 7 days a week + unlimited self-help ice baths',
      prices: {
        '1 Month': 4130,
        '3 Month': 11151,
        '6 Month': 21063,
        '12 Month': 37170,
      },
      popular: true,
    },
    {
      name: 'Pro Classes - 3 Days / Week',
      description: 'Pro classes (3 days/week) + gym access + unlimited self-help ice baths',
      prices: {
        '1 Month': 5150,
        '3 Month': 13500,
        '6 Month': 25500,
        '12 Month': 48000,
      },
      requiresApproval: true,
    },
    {
      name: 'Pro Classes - 5 Days / Week',
      description: 'Pro classes (5 days/week) + gym access + unlimited self-help ice baths',
      prices: {
        '1 Month': 6500,
        '3 Month': 18000,
        '6 Month': 33000,
        '12 Month': 60000,
      },
      requiresApproval: true,
    },
  ];

  const kidsClasses = [
    { name: 'Kids Kickboxing or Karate - 2 Classes/Week', price: 2000 },
    { name: 'Kids Kickboxing or Karate - 4 Classes/Week', price: 2500 },
  ];

  return (
    <div className="bg-cobra-black min-h-screen">
      {/* Pricing Calculator Modal */}
      {showCalculator && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl">
            <button
              onClick={() => setShowCalculator(false)}
              className="absolute -top-12 right-0 text-white/60 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
            <PricingCalculator onComplete={() => setShowCalculator(false)} />
          </div>
        </div>
      )}

      {/* Hero */}
      <section
        className="relative h-[60vh] flex items-center justify-center overflow-hidden"
        data-testid="membership-hero"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/4162451/pexels-photo-4162451.jpeg?auto=compress&cs=tinysrgb&w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-cobra-black/85" />
        <div className="relative z-10 text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="font-anton text-5xl md:text-7xl lg:text-8xl text-white uppercase tracking-wider mb-4"
            data-testid="membership-title"
          >
            MEMBERSHIP <span className="text-cobra-red text-shadow-glow">2026</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-manrope text-xl md:text-2xl text-white/70 max-w-3xl mx-auto mb-8"
          >
            Choose the perfect plan for your fitness journey
          </motion.p>
          <motion.button
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCalculator(true)}
            className="bg-cobra-red hover:bg-red-700 text-white font-bold px-8 py-4 rounded-sm inline-flex items-center gap-3 transition-colors"
            data-testid="find-plan-btn"
          >
            <Calculator size={20} />
            Find My Perfect Plan
          </motion.button>
        </div>
      </section>

      {/* What You Get When You Join — perks block */}
      <MembershipPerks compact={true} />

      {/* Adult Memberships */}
      <section className="py-20 md:py-32 px-4 md:px-8 lg:px-12" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <h2 className="font-anton text-3xl md:text-4xl text-white uppercase tracking-wider mb-2">Adult Memberships</h2>
            <div className="h-1 w-24 bg-cobra-red mb-12" />
          </AnimatedSection>

          <div className="space-y-8">
            {membershipPlans.map((plan, planIndex) => (
              <AnimatedSection key={plan.name} delay={planIndex * 0.1}>
                <div className={`bg-cobra-dark/50 backdrop-blur-sm border rounded-sm overflow-hidden ${
                  plan.popular ? 'border-cobra-red box-shadow-glow' : 'border-white/10'
                }`}>
                  {plan.popular && (
                    <div className="bg-cobra-red text-white text-center py-2 font-bold uppercase text-sm tracking-wider">
                      MOST POPULAR
                    </div>
                  )}
                  
                  <div className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                      <div className="mb-4 md:mb-0">
                        <h3 className="font-anton text-2xl md:text-3xl text-white uppercase tracking-wide mb-2">
                          {plan.name}
                        </h3>
                        {plan.requiresApproval && (
                          <div className="flex items-center space-x-2 text-cobra-red text-sm font-manrope">
                            <AlertCircle size={16} />
                            <span className="uppercase font-bold">Approval Required</span>
                          </div>
                        )}
                        <p className="font-manrope text-white/60 text-sm leading-relaxed max-w-2xl">
                          {plan.description}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(plan.prices).map(([duration, price]) => (
                        <div key={duration} className="bg-cobra-black/50 p-4 rounded-sm border border-white/5 text-center">
                          <div className="font-bebas text-sm text-white/60 mb-1">{duration}</div>
                          <div className="font-bebas text-3xl text-cobra-red">₹{price.toLocaleString('en-IN')}</div>
                          {duration !== '1 Month' && (
                            <div className="text-xs text-white/40 mt-1">
                              ₹{Math.round(price / parseInt(duration)).toLocaleString('en-IN')}/month
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Kids Classes */}
      <section className="py-20 md:py-32 px-4 md:px-8 lg:px-12 bg-gradient-to-b from-cobra-black via-cobra-dark to-cobra-black" data-testid="kids-section">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <h2 className="font-anton text-3xl md:text-4xl text-white uppercase tracking-wider mb-2">Kids Classes</h2>
            <div className="h-1 w-24 bg-cobra-red mb-12" />
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {kidsClasses.map((kidClass, index) => (
              <AnimatedSection key={kidClass.name} delay={index * 0.1}>
                <div className="bg-cobra-dark/50 backdrop-blur-sm border border-white/10 rounded-sm p-6">
                  <h3 className="font-anton text-xl text-white uppercase tracking-wide mb-3">
                    {kidClass.name}
                  </h3>
                  <div className="font-bebas text-4xl text-cobra-red">₹{kidClass.price.toLocaleString('en-IN')}</div>
                  <div className="text-sm text-white/60 font-manrope mt-1">per month</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-20 px-4 md:px-8 lg:px-12" data-testid="notes-section">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="bg-cobra-dark/30 border border-white/10 rounded-sm p-8">
              <h3 className="font-anton text-2xl text-white uppercase tracking-wide mb-6 flex items-center space-x-2">
                <AlertCircle className="text-cobra-red" />
                <span>Important Information</span>
              </h3>
              <ul className="space-y-3 font-manrope text-white/70">
                <li className="flex items-start space-x-3">
                  <Check className="text-cobra-red flex-shrink-0 mt-0.5" size={18} />
                  <span>Prices shown are exclusive of GST; 5% GST is added at checkout</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cobra-red flex-shrink-0 mt-0.5" size={18} />
                  <span>Female members receive 4 extra membership days per month</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cobra-red flex-shrink-0 mt-0.5" size={18} />
                  <span>Third-party workshops may incur additional charges</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cobra-red flex-shrink-0 mt-0.5" size={18} />
                  <span>Ice baths available: Friday (8am-7pm), Saturday (9am-12pm & 5pm-7pm), Sunday (9am-12pm)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cobra-red flex-shrink-0 mt-0.5" size={18} />
                  <span>Ice baths must be requested at the front desk in advance</span>
                </li>
              </ul>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="font-anton text-4xl md:text-5xl text-white uppercase tracking-wider mb-8">
              READY TO <span className="text-cobra-red">START?</span>
            </h2>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(164, 0, 3, 0.6)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/?scroll=form')}
              className="bg-cobra-red text-white hover:bg-red-800 uppercase font-bold tracking-wider rounded-sm px-10 py-5 text-lg transition-all duration-300 inline-flex items-center space-x-3 box-shadow-glow"
            >
              <span>GET YOUR FREE TRIAL</span>
            </motion.button>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Membership;
